import { ANDRAGOGY_HEADING, ANDRAGOGY_PRINCIPLES } from '@/data/experience';
import { ScrollReveal, SplitReveal } from '@/components/motion';

/**
 * AndragogicalPrinciples (/experience — section 3 ★ accreditation-mandated).
 *
 * The five andragogy principles as a calm, numbered academic sequence: an
 * eyebrow + SplitReveal headline over a neutral one-line intro, then the
 * verbatim ANDRAGOGY_HEADING rendered as a prominent display sub-heading, then
 * the five ANDRAGOGY_PRINCIPLES as ScrollReveal-staggered rows — each a plain
 * card with the icon in a navy chip, a large gold "0N" numeral, the verbatim
 * title and the verbatim body.
 *
 * VERBATIM (hard rule): ANDRAGOGY_HEADING and every principle title + body are
 * accreditation copy (Appendix C) and are reproduced exactly — including the
 * source's "his/he" phrasing and its "subject-centeredness"/"problem-centredness"
 * spellings. Nothing is reworded, truncated or "corrected".
 *
 * Motion: SplitReveal headline, ScrollReveal child-stagger for the sequence,
 * plain cards with a quiet hover lift (no TiltCard around text). All primitives
 * are reduced-motion safe and never strand content hidden.
 */
export function AndragogicalPrinciples() {
  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + intro ─────────────────────────────────────────── */}
        <header className="mx-auto max-w-2xl text-center">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Andragogical principles
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            How adults learn best — and how we teach.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              Our teaching is built on the science of how adults learn, expressed through
              five guiding principles.
            </p>
          </ScrollReveal>
        </header>

        {/* ── Verbatim accreditation sub-heading ──────────────────────── */}
        <ScrollReveal from="up" delay={0.15}>
          <p className="mx-auto mt-10 max-w-3xl text-center font-display text-2xl leading-snug font-semibold text-brand-navy md:text-3xl">
            {ANDRAGOGY_HEADING}
          </p>
        </ScrollReveal>

        {/* ── The five principles (verbatim) ──────────────────────────── */}
        <ScrollReveal stagger={0.1} className="mx-auto mt-12 flex max-w-4xl flex-col gap-5">
          {ANDRAGOGY_PRINCIPLES.map((principle, index) => (
            <div
              key={principle.title}
              className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-7">
                {/* Icon chip + numeral */}
                <div className="flex shrink-0 items-center gap-4">
                  <div className="flex size-12 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                    <principle.Icon aria-hidden className="size-6" />
                  </div>
                  <span
                    aria-hidden
                    className="font-display text-4xl leading-none font-semibold text-brand-gold md:text-5xl"
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>

                {/* Verbatim title + body */}
                <div className="min-w-0 sm:border-l sm:border-border sm:pl-7">
                  <h3 className="font-display text-xl leading-tight font-semibold text-foreground">
                    {principle.title}
                  </h3>
                  <p className="mt-2 text-base leading-relaxed text-muted-foreground">
                    {principle.body}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </ScrollReveal>
      </div>
    </section>
  );
}
